/**
 * React hook for automatic logout when TTL expires
 * Monitors TTL expiration and triggers logout automatically
 */
import { useEffect, useRef } from 'react';
import { isAuthenticated, logout } from '../services/auth.service';
import { getRemainingTTL, isTTLExpired } from '../utils/storage.utils';

interface UseAutoLogoutOptions {
  /** Enable automatic logout (default: true) */
  enabled?: boolean;
  /** Interval to check for TTL expiration in milliseconds (default: 60000 - 1 minute) */
  checkInterval?: number;
  /** Callback when automatic logout is triggered */
  onAutoLogout?: () => void;
}

/**
 * Hook to automatically logout user when TTL expires
 * Checks periodically if TTL has expired and triggers logout
 */
export const useAutoLogout = (options: UseAutoLogoutOptions = {}): void => {
  const { enabled = true, checkInterval = 60000, onAutoLogout } = options;

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isLoggingOut = useRef(false);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const checkAndLogout = async () => {
      // Skip if already logging out
      if (isLoggingOut.current) {
        return;
      }

      // Check if user should be authenticated
      if (!isAuthenticated()) {
        return;
      }

      // Check if TTL has expired
      if (isTTLExpired()) {
        // Set flag to prevent multiple logout calls
        isLoggingOut.current = true;

        try {
          // Trigger callback before logout
          onAutoLogout?.();

          // Perform logout
          await logout();

          // Redirect to login page
          globalThis.location.href = '/';
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Auto-logout failed:', error);
          // Still redirect even if logout fails
          globalThis.location.href = '/';
        } finally {
          isLoggingOut.current = false;
        }
      }

      // Also check if TTL is very close to expiring (less than 1 second)
      const remainingTTL = getRemainingTTL();
      if (remainingTTL > 0 && remainingTTL < 1000) {
        // Schedule logout for when TTL actually expires
        setTimeout(() => {
          checkAndLogout();
        }, remainingTTL + 100); // Add 100ms buffer
      }
    };

    // Initial check
    checkAndLogout();

    // Set up interval for periodic checks
    intervalRef.current = setInterval(checkAndLogout, checkInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enabled, checkInterval, onAutoLogout]);
};
